import Icon, { Colors, IconProps } from "@builder/components/general/Icon";
import "./style.scss";
import classNames from "classnames";
import OliverText, { TextProps, TextType } from "@builder/components/general/OliverText";
import { useTranslation } from "react-i18next";
import { formatTestId } from "@builder/utils/format";

//Function to get props used for text inside button
const getTextProps = (text: string, buttonStyle: string, buttonSize: string, disabled: boolean): TextProps => {
    const textProps: TextProps = {
        text: text
    };

    switch (buttonStyle) {
        case "baby-blue":
            textProps.color = "blue";
            break;
        case "primary":
            textProps.color = "white";
            break;
        case "secondary":
            textProps.color = disabled ? "white" : "blue";
            break;
        case "red":
            textProps.color = "white";
            break;
        case "outline":
        case "arrow":
            textProps.color = disabled ? "disabled" : "blue";
            break;
    }

    switch (buttonSize) {
        case "fit-small":
            textProps.size = "xxs";
            break;
        case "small":
            textProps.size = "s";
            textProps.weight = "medium";
            break;
        case "large":
            textProps.size = "l";
            textProps.weight = "light";
            break;
        case "fit":
            textProps.size = "m";
            textProps.weight = "medium";
            break;
    }

    return textProps;
};

//Function to get props used for icon inside button
const getIconProps = (
    buttonStyle: string,
    icon: {
        src: string;
        side?: "start" | "end";
        filter?: Colors;
    },
    disabled: boolean
): IconProps => {
    const iconProps: IconProps = {
        src: icon.src,
        containerStyle: { height: "100%", aspectRatio: "1/1" },
        size: "fit"
    };

    if (icon.filter) {
        iconProps.filter = icon.filter;
    } else {
        switch (buttonStyle) {
            case "primary":
                iconProps.filter = "white";
                break;
            case "secondary":
                iconProps.filter = disabled ? "white" : "blue";
                break;
            case "outline":
                iconProps.filter = disabled ? "disabled" : "blue";
                break;
        }
    }

    return iconProps;
};

interface ButtonProps {
    buttonStyle: "primary" | "secondary" | "outline" | "arrow" | "transparent" | "red" | "grey" | "baby-blue";
    size?: "small" | "large" | "fit" | "full" | "fit-small";
    text?: string;
    icon?: {
        src: string;
        side?: "start" | "end";
        filter?: Colors;
    };
    disabled?: boolean;
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    onClick?: ((e: React.MouseEvent) => void) | (() => void);
    [key: `data-${string}`]: string;
    textType?: TextType;
}

const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
    const { t } = useTranslation();

    const { buttonStyle, size, text, icon, disabled, id, className, style, onClick, textType, ...rest } = props;
    const buttonClass = classNames("oliver-button", {
        [`style-${buttonStyle}`]: buttonStyle,
        [`size-${size}`]: size,
        [`${className}`]: className
    });

    return (
        <button
            id={id}
            className={buttonClass}
            style={style}
            onClick={onClick}
            disabled={disabled}
            data-testid={formatTestId("button", text || "")}
            {...rest}
        >
            {icon?.src && icon.side !== "end" && <Icon {...getIconProps(buttonStyle, icon, disabled || false)} />}
            {text && (
                <OliverText
                    {...getTextProps(t("generalComponents.button." + text), buttonStyle, size || "large", disabled || false)}
                    type={textType}
                />
            )}
            {icon?.src && icon.side === "end" && <Icon {...getIconProps(buttonStyle, icon, disabled || false)} />}
        </button>
    );
};

export default Button;
